import config from "@/config";
import { wagmiConfig } from "@/provider/PrivyProvider";
import Axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { getCookie } from "typescript-cookie";
import { getChainId } from "@wagmi/core";
import type { Config } from "wagmi";
import chainSupport, { type IChainSupport } from "@/utils/convert/chainSupport";

export const AXIOS_INSTANCE = Axios.create({
  baseURL: config.NETWORK_CONFIG.API_BASE_URL,
});

// Add request interceptor to attach token
AXIOS_INSTANCE.interceptors.request.use((config) => {
  const token = getCookie("accessToken");
  // Get the current chainId for each request
  const chainId = getChainId(wagmiConfig as Config);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (chainSupport({ chainId } as IChainSupport)) {
    config.headers["x-chain-id"] = chainId;
  }
  return config;
});

// Global event emitters for errors
export const corsErrorEvent = new EventTarget();
export const serverErrorEvent = new EventTarget();

// Track if error notifications have already been shown
// to prevent multiple notifications
let corsErrorNotificationShown = false;
let serverErrorNotificationShown = false;

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({ ...config, cancelToken: source.token })
    .then(({ data }) => data)
    .catch((error) => {
      // Check for server errors (5xx)
      const isServerError = error.response?.status >= 500;
      if (isServerError && !serverErrorNotificationShown) {
        // Set flag to prevent multiple notifications
        serverErrorNotificationShown = true;
        
        // Dispatch server error event (only once)
        serverErrorEvent.dispatchEvent(new CustomEvent('server-error', { 
          detail: { 
            message: "Server error occurred. Redirecting to maintenance page.",
            error 
          } 
        }));
        
        // Reset the flag after some time
        setTimeout(() => {
          serverErrorNotificationShown = false;
        }, 30000); // Longer timeout for server errors
      }
      
      // Check for CORS errors
      const isCorsError = 
        error.message?.includes('CORS') || 
        error.name === 'NetworkError' || 
        (error.message && /cross-origin|CORS policy|Access-Control-Allow/i.test(error.message)) ||
        (error.code === 'ERR_NETWORK');
      
      if (isCorsError && !corsErrorNotificationShown) {
        // Set flag to prevent multiple notifications
        corsErrorNotificationShown = true;
        
        // Dispatch CORS error event (only once)
        corsErrorEvent.dispatchEvent(new CustomEvent('cors-error', { 
          detail: { 
            message: "Server is currently rebuilding. Please try again later.",
            error 
          } 
        }));
        
        // Reset the flag after some time (e.g., 10 seconds) to allow 
        // future notifications if the issue persists after a delay
        setTimeout(() => {
          corsErrorNotificationShown = false;
        }, 10000);
      }
      
      // Re-throw the error for React Query to handle
      throw error;
    });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel("Query was cancelled by React Query");
  };

  return promise;
};

export default customInstance;

export interface ErrorType<Error> extends AxiosError<Error> {}
